import { useRef ,  useCallback } from 'react';
import { useSignalREvent } from '@/hooks/use-signalr-event';
import type { 
  BotEvent, 
  ProcessingUpdateEvent, 
  ToolExecutionEvent, 
  BotErrorEvent, 
  LongRunningOperationEvent 
} from '@/types/signalr';

interface BotEventHandlers {
  onProcessingUpdate?: (event: ProcessingUpdateEvent) => void;
  onToolExecution?: (event: ToolExecutionEvent) => void;
  onError?: (event: BotErrorEvent) => void;
  onLongRunningOperation?: (event: LongRunningOperationEvent) => void;
}

/**
 * Hook to handle different types of bot events from SignalR
 * @param handlers Object containing handlers for each event type
 */
export function useBotEvents(handlers: BotEventHandlers) {
  // Store refs to handlers to avoid recreating the event handler
  const handlersRef = useRef<BotEventHandlers>(handlers);
  handlersRef.current = handlers;

  const handleBotEvent = useCallback((eventOrWrapper: BotEvent | { data: BotEvent }) => {
    // Handle both direct events and wrapped events
    const event = 'data' in eventOrWrapper ? eventOrWrapper.data : eventOrWrapper;
    const { eventType } = event;
    const currentHandlers = handlersRef.current;
    
    console.log('useBotEvents: Received event', { eventType, event });

    switch (eventType) {
      case 'ProcessingUpdate':
        if (currentHandlers.onProcessingUpdate) {
          currentHandlers.onProcessingUpdate(event as ProcessingUpdateEvent);
        }
        break;
      
      case 'ToolExecution':
        if (currentHandlers.onToolExecution) {
          currentHandlers.onToolExecution(event as ToolExecutionEvent);
        }
        break;
      
      case 'Error':
        if (currentHandlers.onError) {
          currentHandlers.onError(event as BotErrorEvent);
        }
        break;
      
      case 'LongRunningOperation':
        if (currentHandlers.onLongRunningOperation) {
          currentHandlers.onLongRunningOperation(event as LongRunningOperationEvent);
        }
        break;
      
      default:
        console.warn('Unknown bot event type:', eventType);
    }
  }, []);

  // Subscribe to bot events
  useSignalREvent('ReceiveBotEvent', handleBotEvent);
}

/**
 * Common error codes for bot errors
 */
export const BOT_ERROR_CODES = {
  OPENAI_API_ERROR_400: 'OPENAI_API_ERROR_400',
  OPENAI_API_ERROR_401: 'OPENAI_API_ERROR_401',
  OPENAI_API_ERROR_429: 'OPENAI_API_ERROR_429',
  BOT_STREAMING_ERROR: 'BOT_STREAMING_ERROR',
  BOT_ERROR: 'BOT_ERROR',
  PROCESSING_ERROR: 'PROCESSING_ERROR',
  NO_RUNNER: 'NO_RUNNER',
  CONFIG_CHECK_FAILED: 'CONFIG_CHECK_FAILED',
  STREAMING_ERROR: 'STREAMING_ERROR',
} as const;

/**
 * Check if an error is user-facing
 */
export function isUserFacingError(error: BotErrorEvent): boolean {
  return error.isUserFacing;
}

/**
 * Get a user-friendly message for common error codes
 */
export function getUserFriendlyErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case BOT_ERROR_CODES.OPENAI_API_ERROR_429:
      return "I've reached my processing limit for now. Please try again in a few minutes.";
    case BOT_ERROR_CODES.OPENAI_API_ERROR_401:
      return "There's an authentication issue. Please contact support.";
    case BOT_ERROR_CODES.NO_RUNNER:
      return "The assistant is currently unavailable. Please try again later.";
    case BOT_ERROR_CODES.CONFIG_CHECK_FAILED:
      return "The assistant configuration needs to be updated. Please contact support.";
    default:
      return "Something went wrong. Please try again or contact support if the issue persists.";
  }
}